import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FilePdf, Eye, ArrowLeft, Palette } from "@phosphor-icons/react";
import { SectionEditor } from "@/components/report/SectionEditor";
import { ContentPickerModal } from "@/components/report/ContentPickerModal";
import { PreviewModal } from "@/components/report/PreviewModal";
import { SectionManager } from "@/components/report/SectionManager";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";

export default function ReportBuilderPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showContentPicker, setShowContentPicker] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [generatingNote, setGeneratingNote] = useState(false);

  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [pickerFilter, setPickerFilter] = useState("");
  const [pickerTypeFilter, setPickerTypeFilter] = useState("");
  const [pickerStatusFilter, setPickerStatusFilter] = useState("");

  const [dragIdx, setDragIdx] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      const resp = await api.get(`/reports/${id}`);
      setReport(resp.data);
    } catch {
      toast.error("Report not found");
      navigate("/reports");
    }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    if (report && !selectedId && report.sections?.length > 0) {
      setSelectedId(report.sections[0].section_id);
    }
  }, [report, selectedId]);

  const sections = (report?.sections || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const selectedSection = sections.find((s) => s.section_id === selectedId);

  const updateReport = async (updates) => {
    try {
      const resp = await api.patch(`/reports/${id}`, updates);
      setReport(resp.data);
    } catch {
      toast.error("Failed to update report");
    }
  };

  const addSection = async (sectionType, title = "", historyItemId = null) => {
    try {
      const resp = await api.post(`/reports/${id}/sections`, {
        section_type: sectionType,
        title: title || sectionType.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        history_item_id: historyItemId,
      });
      await fetchReport();
      setSelectedId(resp.data.section_id);
      setAddMenuOpen(false);
    } catch {
      toast.error("Failed to add section");
    }
  };

  const updateSection = async (sectionId, updates) => {
    try {
      await api.patch(`/reports/${id}/sections/${sectionId}`, updates);
      setReport((prev) => {
        if (!prev) return prev;
        return { ...prev, sections: prev.sections.map((s) => s.section_id === sectionId ? { ...s, ...updates } : s) };
      });
    } catch {
      toast.error("Failed to update section");
    }
  };

  const deleteSection = async (sectionId) => {
    try {
      await api.delete(`/reports/${id}/sections/${sectionId}`);
      if (selectedId === sectionId) setSelectedId(null);
      await fetchReport();
      toast.success("Section removed");
    } catch {
      toast.error("Failed to delete section");
    }
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const reordered = [...sections];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    const sectionIds = reordered.map((s) => s.section_id);
    setDragIdx(null);
    try {
      await api.post(`/reports/${id}/sections/reorder`, { section_ids: sectionIds });
      await fetchReport();
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const openContentPicker = async () => {
    setShowContentPicker(true);
    setHistoryLoading(true);
    setSelectedItems([]);
    try {
      const resp = await api.get(`/history?business_id=${report?.business_id || ""}&limit=200`);
      setHistoryItems(resp.data.items || []);
    } catch { /* non-critical */ }
    setHistoryLoading(false);
  };

  const addSelectedItems = async () => {
    for (const item of selectedItems) {
      await addSection("content_item", item.topic || item.title || "Content", item.history_id);
    }
    setShowContentPicker(false);
    setSelectedItems([]);
    toast.success(`${selectedItems.length} item(s) added`);
  };

  const togglePickerItem = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.history_id === item.history_id);
      return exists ? prev.filter((i) => i.history_id !== item.history_id) : [...prev, item];
    });
  };

  const generateStrategyNote = async () => {
    if (!selectedSection) return;
    setGeneratingNote(true);
    try {
      const resp = await api.post(`/reports/${id}/generate-strategy-note`, { section_id: selectedSection.section_id });
      setReport((prev) => ({
        ...prev,
        sections: prev.sections.map((s) => s.section_id === selectedSection.section_id ? { ...s, body: resp.data.generated_text } : s),
      }));
      toast.success("Strategy note generated");
    } catch (err) {
      toast.error(err.response?.data?.error_message || "Failed to generate strategy note");
    }
    setGeneratingNote(false);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const resp = await api.post(`/reports/${id}/export-pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(report?.client_name || "Report").replace(/[^a-zA-Z0-9_-]/g, "_")}_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exported!");
    } catch {
      toast.error("PDF export failed");
    }
    setExporting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="h-[600px] bg-[var(--bg-card)] rounded-lg animate-pulse border border-[var(--border)]" />
        </div>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4" data-testid="report-builder-topbar">
          <div className="flex items-center gap-3 min-w-0 w-full sm:flex-1">
            <button onClick={() => navigate("/reports")} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors shrink-0" data-testid="back-to-reports">
              <ArrowLeft size={18} />
            </button>
            <input
              type="text"
              value={report.title || ""}
              onChange={(e) => setReport((prev) => ({ ...prev, title: e.target.value }))}
              onBlur={() => updateReport({ title: report.title })}
              className="text-base sm:text-lg font-bold text-[var(--text-primary)] bg-transparent border-none outline-none flex-1 min-w-0"
              data-testid="report-title-editable"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
            <Select value={report.status} onValueChange={(v) => updateReport({ status: v })}>
              <SelectTrigger className="w-[110px] h-10 sm:h-8 text-xs bg-[var(--bg-card)] border-[var(--border)]" data-testid="report-status-toggle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <button onClick={() => setShowColors(!showColors)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" data-testid="brand-colors-btn">
                <Palette size={14} />
                <span className="w-3 h-3 rounded-full border border-[var(--border)]" style={{ backgroundColor: report.brand_colors?.primary || "#FF4400" }} />
              </button>
              {showColors && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--bg-raised)] border border-[var(--border-strong)] rounded-lg p-3 shadow-lg space-y-2 w-48" data-testid="brand-colors-popover">
                  <div>
                    <label className="text-[10px] text-[var(--text-muted)]">Primary</label>
                    <input type="color" value={report.brand_colors?.primary || "#FF4400"} onChange={(e) => setReport((p) => ({ ...p, brand_colors: { ...p.brand_colors, primary: e.target.value } }))} onBlur={() => updateReport({ brand_colors: report.brand_colors })} className="w-full h-8 rounded cursor-pointer" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[var(--text-muted)]">Secondary</label>
                    <input type="color" value={report.brand_colors?.secondary || "#1A1A2E"} onChange={(e) => setReport((p) => ({ ...p, brand_colors: { ...p.brand_colors, secondary: e.target.value } }))} onBlur={() => updateReport({ brand_colors: report.brand_colors })} className="w-full h-8 rounded cursor-pointer" />
                  </div>
                </div>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowPreview(true)} className="h-10 sm:h-8 text-xs border-[var(--border)]" data-testid="preview-btn">
              <Eye size={14} className="mr-1" /> Preview
            </Button>
            <Button onClick={handleExportPdf} disabled={exporting} className="h-10 sm:h-8 text-xs bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="export-pdf-btn">
              <FilePdf size={14} className="mr-1" /> {exporting ? "Exporting..." : "Export PDF"}
            </Button>
          </div>
        </div>

        <div className="mb-4"><AlwaysIncludeIndicator /></div>

        <div className="flex flex-col lg:flex-row gap-4 min-h-[calc(100vh-180px)]" data-testid="report-builder-panels">
          <div className="lg:hidden">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
              {sections.map((sec) => {
                const isSelected = sec.section_id === selectedId;
                return (
                  <button key={sec.section_id} onClick={() => setSelectedId(sec.section_id)}
                    className={`shrink-0 px-4 py-2.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
                      isSelected ? "bg-[#FF4400]/10 border-[#FF4400]/30 text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border)] text-[var(--text-secondary)]"
                    }`} data-testid={`mobile-section-${sec.section_id}`}>
                    {sec.title || sec.section_type.replace("_", " ")}
                  </button>
                );
              })}
              <button onClick={() => setAddMenuOpen(!addMenuOpen)}
                className="shrink-0 px-4 py-2.5 rounded-full text-xs font-medium border border-dashed border-[var(--border)] text-[var(--text-muted)] hover:text-[#FF4400] hover:border-[#FF4400]/30"
                data-testid="mobile-add-section-btn">+ Add</button>
            </div>
          </div>

          <div className="hidden lg:block">
            <SectionManager
              sections={sections}
              selectedId={selectedId}
              onSelectSection={setSelectedId}
              onDeleteSection={deleteSection}
              onDragStart={setDragIdx}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              addMenuOpen={addMenuOpen}
              onToggleAddMenu={() => setAddMenuOpen(!addMenuOpen)}
              onAddSection={addSection}
              onOpenContentPicker={openContentPicker}
            />
          </div>

          <div className="flex-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-y-auto" data-testid="section-editor">
            {selectedSection ? (
              <div className="p-6">
                <SectionEditor
                  section={selectedSection}
                  report={report}
                  onUpdate={(updates) => updateSection(selectedSection.section_id, updates)}
                  onGenerateNote={generateStrategyNote}
                  generatingNote={generatingNote}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-[var(--text-muted)]">
                Select a section to edit
              </div>
            )}
          </div>
        </div>

        <ContentPickerModal
          open={showContentPicker}
          onOpenChange={setShowContentPicker}
          historyItems={historyItems}
          historyLoading={historyLoading}
          selectedItems={selectedItems}
          onToggleItem={togglePickerItem}
          onAddItems={addSelectedItems}
          pickerFilter={pickerFilter}
          onPickerFilterChange={setPickerFilter}
          pickerTypeFilter={pickerTypeFilter}
          onPickerTypeFilterChange={setPickerTypeFilter}
          pickerStatusFilter={pickerStatusFilter}
          onPickerStatusFilterChange={setPickerStatusFilter}
        />

        <PreviewModal
          open={showPreview}
          onOpenChange={setShowPreview}
          report={report}
          sections={sections}
          onExportPdf={handleExportPdf}
        />
      </main>
    </div>
  );
}
